import {
  getCachedInstitutionData,
  saveCachedInstitutionData,
  clearCachedInstitutionData,
} from "./institutionDataCache";

const AUTH_API_URL =
  process.env.REACT_APP_AUTH_COACHING_API_URL ||
  "https://us-central1-pencil-backend.cloudfunctions.net/auth-coaching-dashboard-db-prod";

const DEFAULT_PAGE_SIZE = 50;

function mergeSessionsByRecordingId(existingList, incomingList) {
  const map = new Map();
  (existingList || []).forEach((s) => {
    if (s?.recording_id) {
      map.set(s.recording_id, s);
    }
  });
  (incomingList || []).forEach((s) => {
    if (s?.recording_id) {
      map.set(s.recording_id, s);
    }
  });
  return Array.from(map.values());
}

export const authService = {
  async login(password) {
    try {
      const response = await fetch(AUTH_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (data.auth) {
        localStorage.setItem("isAuthenticated", "true");
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      return { success: false, message: "Network error. Please try again." };
    }
  },

  logout() {
    localStorage.removeItem("isAuthenticated");
  },

  isAuthenticated() {
    return localStorage.getItem("isAuthenticated") === "true";
  },

  async verifyInstitution(institutionId, password) {
    try {
      const response = await fetch(`${AUTH_API_URL}/auth/${institutionId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error("Institution authentication failed");
      }

      const data = await response.json();

      if (data.auth) {
        localStorage.setItem(`auth_${institutionId}`, "true");
        return data;
      } else {
        throw new Error(data.message || "Authentication failed");
      }
    } catch (error) {
      console.error("Institution auth verification error:", error);
      throw error;
    }
  },

  isInstitutionAuthenticated(institutionId) {
    return localStorage.getItem(`auth_${institutionId}`) === "true";
  },

  logoutInstitution(institutionId) {
    localStorage.removeItem(`auth_${institutionId}`);
    clearCachedInstitutionData(institutionId);
  },

  async fetchInstitutionData(institutionId) {
    try {
      const response = await fetch(`${AUTH_API_URL}/data/${institutionId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch institution data");
      }

      return await response.json();
    } catch (error) {
      console.error("Data fetch error:", error);
      throw error;
    }
  },

  async fetchAllInstitutionData(institutionId, onProgress) {
    try {
      const cached = await getCachedInstitutionData(institutionId);
      const cachedMeta = cached?.meta;
      const cachedSessions = cached?.sessions?.length ? cached.sessions : [];

      const fetchPage = async (pageNumber) => {
        const response = await fetch(
          `${AUTH_API_URL}/data/${institutionId}?pageNumber=${pageNumber}&pageSize=${DEFAULT_PAGE_SIZE}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch institution data");
        }
        return response.json();
      };

      const emit = (sessions, paginationSlice, extra = {}) => {
        if (!onProgress) {
          return;
        }
        const {
          pageNumber,
          pageSize,
          totalDocuments,
          totalPages,
          hasNextPage,
        } = paginationSlice;
        onProgress({
          page: pageNumber,
          totalPages,
          pageSize: pageSize || DEFAULT_PAGE_SIZE,
          loadedDocuments: sessions.length,
          totalDocuments,
          currentData: sessions,
          hasNextPage: hasNextPage === true,
          ...extra,
        });
      };

      // While re-validating / fetching additional pages, show progress UI even
      // before the first network response (sessions already on screen from cache).
      if (onProgress && cachedSessions.length > 0) {
        const fromMeta = cachedMeta?.totalDocuments;
        const totalForBar =
          fromMeta != null && fromMeta > 0
            ? fromMeta
            : Math.max(cachedSessions.length, 1);
        emit(
          [...cachedSessions],
          {
            pageNumber: 0,
            pageSize: cachedMeta?.pageSize || DEFAULT_PAGE_SIZE,
            totalDocuments: Math.max(totalForBar, cachedSessions.length),
            totalPages: cachedMeta?.totalPages ?? 1,
            hasNextPage: true,
          },
          { isBackgroundSync: true }
        );
      }

      const first = await fetchPage(1);
      const { data: firstData = [], pagination: firstPagination = {} } = first;
      const {
        pageSize: pageSizeRaw,
        totalDocuments = 0,
        totalPages = 1,
        pageNumber: firstPageNumber = 1,
        hasNextPage: firstHasNext,
      } = firstPagination;
      const pageSize = pageSizeRaw || DEFAULT_PAGE_SIZE;

      const sameShape =
        cachedMeta &&
        cachedMeta.totalDocuments === totalDocuments &&
        cachedMeta.totalPages === totalPages &&
        cachedMeta.pageSize === pageSize;

      if (
        sameShape &&
        cachedMeta.lastFetchedPage >= totalPages &&
        cachedSessions.length === totalDocuments
      ) {
        emit(cachedSessions, {
          pageNumber: firstPageNumber,
          pageSize,
          totalDocuments,
          totalPages,
          hasNextPage: false,
        });
        return cachedSessions;
      }

      let sessions = sameShape ? [...cachedSessions] : [];
      if (!sameShape) {
        sessions = mergeSessionsByRecordingId([], firstData);
        await saveCachedInstitutionData(institutionId, sessions, {
          lastFetchedPage: 1,
          totalDocuments,
          totalPages,
          pageSize,
        });
        emit(sessions, {
          pageNumber: firstPageNumber,
          pageSize,
          totalDocuments,
          totalPages,
          hasNextPage: firstHasNext === true,
        });
        for (let p = 2; p <= totalPages; p += 1) {
          const pageJson = await fetchPage(p);
          const { data = [] } = pageJson;
          sessions = mergeSessionsByRecordingId(sessions, data);
          await saveCachedInstitutionData(institutionId, sessions, {
            lastFetchedPage: p,
            totalDocuments,
            totalPages,
            pageSize,
          });
          emit(sessions, {
            pageNumber: p,
            pageSize,
            totalDocuments,
            totalPages,
            hasNextPage: p < totalPages,
          });
        }
        return sessions;
      }

      const startPage = (cachedMeta.lastFetchedPage || 0) + 1;
      for (let p = startPage; p <= totalPages; p += 1) {
        const pageJson = await fetchPage(p);
        const { data = [] } = pageJson;
        sessions = mergeSessionsByRecordingId(sessions, data);
        await saveCachedInstitutionData(institutionId, sessions, {
          lastFetchedPage: p,
          totalDocuments,
          totalPages,
          pageSize,
        });
        emit(sessions, {
          pageNumber: p,
          pageSize,
          totalDocuments,
          totalPages,
          hasNextPage: p < totalPages,
        });
      }

      return sessions;
    } catch (error) {
      console.error("Data fetch error:", error);
      throw error;
    }
  },
};
